import type { Prisma, PrismaClient } from '@prisma/client';
import type { RequestHandler } from 'express';
import { HttpError } from './errors.js';

/**
 * Safe retries for mobile networks: a POST carrying `Idempotency-Key` is executed once per user;
 * a repeat with the same key replays the stored response instead of creating a duplicate.
 * Place after `authenticate`. Requests without the header are untouched.
 */
export function idempotent(db: PrismaClient): RequestHandler {
  return async (req, res, next) => {
    const key = req.header('idempotency-key');
    if (!key || req.method !== 'POST' || !req.auth) return next();
    if (key.length > 120)
      return next(new HttpError(400, 'BAD_IDEMPOTENCY_KEY', 'Idempotency-Key too long'));
    const userId = req.auth.userId;

    try {
      const existing = await db.idempotencyKey.findUnique({
        where: { userId_key: { userId, key } },
      });
      if (existing) {
        if (existing.method !== req.method || existing.path !== req.originalUrl.split('?')[0]) {
          throw new HttpError(
            422,
            'IDEMPOTENCY_KEY_REUSED',
            'This key was used for a different request',
          );
        }
        if (existing.responseStatus == null) {
          throw new HttpError(
            409,
            'REQUEST_IN_PROGRESS',
            'A request with this key is still being processed',
          );
        }
        res.setHeader('Idempotent-Replay', 'true');
        return res.status(existing.responseStatus).json(existing.responseBody);
      }
      try {
        await db.idempotencyKey.create({
          data: { userId, key, method: req.method, path: req.originalUrl.split('?')[0] ?? '' },
        });
      } catch {
        throw new HttpError(
          409,
          'REQUEST_IN_PROGRESS',
          'A request with this key is still being processed',
        );
      }

      const originalJson = res.json.bind(res);
      res.json = ((body: unknown) => {
        const status = res.statusCode;
        if (status < 500) {
          void db.idempotencyKey
            .update({
              where: { userId_key: { userId, key } },
              data: { responseStatus: status, responseBody: (body ?? {}) as Prisma.InputJsonValue },
            })
            .catch(() => undefined);
        } else {
          // Server errors are retryable: forget the key.
          void db.idempotencyKey
            .delete({ where: { userId_key: { userId, key } } })
            .catch(() => undefined);
        }
        return originalJson(body);
      }) as typeof res.json;
      next();
    } catch (err) {
      next(err);
    }
  };
}
